// LoggedInView.tsx — ChatGPT-style full-screen chat with sidebar
import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiEdit, FiSearch, FiSend, FiExternalLink,
  FiChevronRight, FiLayers, FiTrendingUp, FiCompass,
  FiX, FiTrash2, FiLogOut, FiChevronUp,
} from "react-icons/fi";
import { signOut } from "firebase/auth";
import axios from "axios";
import { BASE_URL } from "../apiConfig";
import { useProgressiveSearch } from "../hooks/useProgressiveSearch";
import type { SearchResult } from "../hooks/useProgressiveSearch";
import { auth } from "../firebase";

// ── Types ─────────────────────────────────────────────────────────────────────
interface Turn {
  id: number;
  query: string;
  sources: SearchResult[];
  cleanAnswer: string | null;
  navHint: string | null;
  navRoute: string | null;
  navLabel: string | null;
  suggestedNames?: string[];
}

// Sidebar metadata only — no turns (fetched on demand, same as Claude.ai)
interface ChatSession {
  id: string;
  title: string;
  timestamp: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const MAX_HISTORY = 50;

const NAV_ROUTE_MAP: Array<[RegExp, string, string]> = [
  [/startup directory|startup search|startup discover/i, "/startups", "Startup Search"],
  [/find vendors?|solution provider/i, "/startups", "Startup Search"],
  [/vc intelligence|venture capital|vc dir/i, "/vc-intelligence", "VC Intelligence"],
  [/m&a dashboard|acquisition dashboard|acquisition target/i, "/acquisition-dashboard", "M&A Dashboard"],
  [/use[- ]case trends?|ai trends?|market trends?/i, "/usecase-trends", "Use Case Trends"],
  [/\bdiscover\b tab|\bdiscover\b page|use[- ]case discover/i, "/discover", "Discover"],
];

function extractNavInfo(raw: string) {
  const marker = "➡️ **Find this in UCDP:**";
  const idx = raw.indexOf(marker);
  if (idx === -1) return { cleanAnswer: raw, navHint: null, navRoute: null, navLabel: null };
  const hint = raw.slice(idx + marker.length).trim();
  const cleanAnswer = raw.slice(0, idx).trim();
  const hintPlain = hint.replace(/\*\*/g, "");
  let navRoute: string | null = null;
  let navLabel: string | null = null;
  for (const [rx, route, label] of NAV_ROUTE_MAP) {
    if (rx.test(hintPlain)) { navRoute = route; navLabel = label; break; }
  }
  return { cleanAnswer, navHint: hint, navRoute, navLabel };
}

function renderNavHint(hint: string): React.ReactNode {
  const parts = hint.split(/\*\*(.*?)\*\*/g);
  return parts.map((p, i) =>
    i % 2 === 1
      ? <strong key={i} className="text-white font-semibold">{p}</strong>
      : <span key={i}>{p}</span>
  );
}

function faviconHost(url?: string) {
  if (!url) return "";
  try { return new URL(url).hostname; } catch { return ""; }
}
function hostLabel(url?: string) {
  if (!url) return "";
  try { return new URL(url).hostname.replace("www.", ""); } catch { return ""; }
}

// ── Markdown renderer ─────────────────────────────────────────────────────────
const MarkdownText = ({ text }: { text: string }) => {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  let i = 0;

  const renderInline = (str: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    let remaining = str;
    let key = 0;
    while (remaining.length > 0) {
      const citMatch = remaining.match(/^([\s\S]*?)\[(\d+)\]/);
      const boldMatch = remaining.match(/^([\s\S]*?)\*\*([\s\S]+?)\*\*/);
      const codeMatch = remaining.match(/^([\s\S]*?)`([^`]+)`/);
      const candidates: Array<[string, number]> = [];
      if (citMatch) candidates.push(["cit", citMatch[1].length]);
      if (boldMatch) candidates.push(["bold", boldMatch[1].length]);
      if (codeMatch) candidates.push(["code", codeMatch[1].length]);
      const winner = candidates.sort((a, b) => a[1] - b[1])[0]?.[0] ?? null;
      if (winner === "cit" && citMatch) {
        if (citMatch[1]) parts.push(<span key={key++}>{citMatch[1]}</span>);
        remaining = remaining.slice(citMatch[0].length);
      } else if (winner === "bold" && boldMatch) {
        if (boldMatch[1]) parts.push(<span key={key++}>{boldMatch[1]}</span>);
        parts.push(<strong key={key++} className="text-white font-semibold">{boldMatch[2]}</strong>);
        remaining = remaining.slice(boldMatch[0].length);
      } else if (winner === "code" && codeMatch) {
        if (codeMatch[1]) parts.push(<span key={key++}>{codeMatch[1]}</span>);
        parts.push(<code key={key++} className="px-1 py-0.5 rounded bg-white/10 text-orange-300 text-[13px] font-mono">{codeMatch[2]}</code>);
        remaining = remaining.slice(codeMatch[0].length);
      } else {
        parts.push(<span key={key++}>{remaining}</span>);
        break;
      }
    }
    return parts;
  };

  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("```")) {
      const lang = line.slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) { codeLines.push(lines[i]); i++; }
      elements.push(<pre key={i} className="my-3 p-3 rounded-lg bg-black/40 border border-white/10 overflow-x-auto text-[13px] font-mono text-green-300 leading-relaxed">{lang && <span className="text-xs text-gray-500 block mb-1">{lang}</span>}{codeLines.join("\n")}</pre>);
    } else if (/^#{1,3} /.test(line)) {
      const level = line.match(/^(#{1,3}) /)?.[1].length ?? 1;
      const content = line.replace(/^#{1,3} /, "");
      const cls = level === 1 ? "text-lg font-bold text-white mt-3 mb-1" : level === 2 ? "text-base font-semibold text-white mt-2 mb-1" : "text-sm font-semibold text-gray-300 mt-1";
      elements.push(<p key={i} className={cls}>{renderInline(content)}</p>);
    } else if (/^[-*] /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*] /.test(lines[i])) { items.push(lines[i].slice(2)); i++; }
      elements.push(<ul key={i} className="my-1 space-y-1 pl-2">{items.map((item, j) => <li key={j} className="flex gap-2"><span className="text-orange-400 mt-0.5 shrink-0">•</span><span>{renderInline(item)}</span></li>)}</ul>);
      continue;
    } else if (/^\d+\. /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) { items.push(lines[i].replace(/^\d+\. /, "")); i++; }
      elements.push(<ol key={i} className="my-1 space-y-1 pl-2">{items.map((item, j) => <li key={j} className="flex gap-2"><span className="text-orange-400 shrink-0 font-mono text-xs mt-0.5">{j + 1}.</span><span>{renderInline(item)}</span></li>)}</ol>);
      continue;
    } else if (line.trim() === "") {
      elements.push(<div key={i} className="h-2" />);
    } else {
      elements.push(<p key={i} className="leading-relaxed">{renderInline(line)}</p>);
    }
    i++;
  }
  return <div className="space-y-1 text-[15px] text-gray-100 leading-relaxed" style={{ fontFamily: "Sora, sans-serif" }}>{elements}</div>;
};

// ── Turn block ────────────────────────────────────────────────────────────────
interface TurnBlockProps {
  turn: Turn;
  index: number;
  navigate: ReturnType<typeof useNavigate>;
  sessionId: string;
  isEditing: boolean;
  anyEditing: boolean;
  onStartEdit: (index: number) => void;
  onCancelEdit: () => void;
  onSubmitEdit: (index: number, newQuery: string) => void;
}

const TurnBlock = ({ turn, index, navigate, sessionId, isEditing, anyEditing, onStartEdit, onCancelEdit, onSubmitEdit }: TurnBlockProps) => {
  const [editText, setEditText] = useState(turn.query);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing) {
      setEditText(turn.query);
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.style.height = "auto";
          textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
          const len = textareaRef.current.value.length;
          textareaRef.current.setSelectionRange(len, len);
        }
      }, 30);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const submit = () => {
    const trimmed = editText.trim();
    if (trimmed) onSubmitEdit(index, trimmed);
  };

  return (
  <div className="mb-8">
    <div className="flex justify-end mb-4 group">
      {isEditing ? (
        <div className="w-full max-w-[75%] flex flex-col gap-2">
          <textarea
            ref={textareaRef}
            value={editText}
            onChange={e => {
              setEditText(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = e.target.scrollHeight + "px";
            }}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
              if (e.key === "Escape") onCancelEdit();
            }}
            rows={1}
            style={{
              width: "100%", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,84,31,0.5)",
              borderRadius: 14, padding: "12px 16px", color: "#f9f9f9", fontSize: 14,
              fontFamily: "Sora, sans-serif", resize: "none", outline: "none",
              lineHeight: 1.5, overflow: "hidden",
              boxShadow: "0 0 0 3px rgba(255,84,31,0.12)",
            }}
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={onCancelEdit}
              style={{ padding: "6px 14px", borderRadius: 8, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#9ca3af", fontSize: 13, cursor: "pointer", fontWeight: 600 }}
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={!editText.trim()}
              style={{ padding: "6px 16px", borderRadius: 8, background: editText.trim() ? "#FF541F" : "rgba(255,84,31,0.3)", border: "none", color: "#fff", fontSize: 13, cursor: editText.trim() ? "pointer" : "not-allowed", fontWeight: 700 }}
            >
              Send ↑
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          {!anyEditing && (
            <button
              onClick={() => onStartEdit(index)}
              title="Edit message"
              className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-white/10"
              style={{ color: "#6b7280", flexShrink: 0 }}
            >
              <FiEdit size={13} />
            </button>
          )}
          <div className="max-w-[70%] px-4 py-3 rounded-2xl rounded-tr-sm text-sm text-white" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.1)" }}>
            {turn.query}
          </div>
        </div>
      )}
    </div>

    <div className="flex items-start gap-3">
      <div className="shrink-0 w-7 h-7 rounded-xl flex items-center justify-center text-white text-[10px] font-bold mt-0.5" style={{ background: "#FF541F" }}>AI</div>
      <div className="flex-1 min-w-0">
        {turn.cleanAnswer ? (
          <>
            <MarkdownText text={turn.cleanAnswer} />

            {(() => {
              const names = turn.suggestedNames ?? extractStartupNames(turn.cleanAnswer);
              if (!names.length) return null;
              return (
                <div className="mt-4">
                  <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "#6b7280" }}>
                    Search in Startup Directory:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {names.map(name => (
                      <button
                        key={name}
                        onClick={() => navigate("/startups", { state: { search_query: name } })}
                        style={{
                          padding: "5px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600,
                          background: "rgba(255,84,31,0.08)", border: "1px solid rgba(255,84,31,0.3)",
                          color: "#FF541F", cursor: "pointer", fontFamily: "Sora, sans-serif",
                          transition: "all 0.15s",
                        }}
                        onMouseOver={e => {
                          e.currentTarget.style.background = "#FF541F";
                          e.currentTarget.style.color = "#fff";
                        }}
                        onMouseOut={e => {
                          e.currentTarget.style.background = "rgba(255,84,31,0.08)";
                          e.currentTarget.style.color = "#FF541F";
                        }}
                      >
                        {name} →
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {turn.navHint && (
              <div className="mt-4 p-3 rounded-xl flex items-start gap-3" style={{ background: "rgba(255,84,31,0.08)", border: "1px solid rgba(255,84,31,0.2)" }}>
                <span className="text-base shrink-0">➡️</span>
                <p className="text-sm text-orange-300 flex-1">{renderNavHint(turn.navHint)}</p>
                {turn.navRoute && (
                  <button
                    onClick={() => {
                      const names = turn.suggestedNames ?? extractStartupNames(turn.cleanAnswer || "");
                      const descs = extractStartupListWithDesc(turn.cleanAnswer || "");
                      const descMap = new Map(descs.map(d => [d.name.toLowerCase(), d.description]));
                      const prefill = names.map(name => ({
                        id: null as null, name,
                        short_description: descMap.get(name.toLowerCase()) || "",
                        website: null, logo_url: null, founded_year: null,
                        headquarters: "", funding_stage: "", total_funding: 0,
                        category: null, region: null, tags: [], data_source: "live",
                      }));
                      sessionStorage.setItem("ucdp_return_session_id", sessionId);
                      navigate(turn.navRoute!, { state: { search_query: turn.query, prefill_results: prefill.length ? prefill : undefined } });
                    }}
                    className="shrink-0 px-3 py-1.5 rounded-lg text-white text-xs font-semibold whitespace-nowrap" style={{ background: "#FF541F" }}>
                    Open {turn.navLabel} <FiChevronRight className="inline w-3 h-3" />
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <span className="text-xs text-gray-600 italic">Answer unavailable</span>
        )}

        {turn.sources.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center gap-1.5 mb-2">
              <FiSearch size={10} className="text-[#DC2626]" />
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                Sources · {turn.sources.length}
              </span>
              <span className="text-xs text-slate-400 ml-1">— web pages used to answer this</span>
            </div>
            <div className="flex flex-wrap gap-2 pb-1">
              {turn.sources.map((r, idx) => (
                <a key={idx} href={r.url} target="_blank" rel="noopener noreferrer"
                  className="shrink-0 flex items-center gap-2 px-2.5 py-1.5 rounded-xl transition-all"
                  style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", maxWidth: 180, minWidth: 100, textDecoration: "none" }}
                  onMouseOver={e => (e.currentTarget.style.border = "1px solid rgba(220,38,38,0.4)")}
                  onMouseOut={e => (e.currentTarget.style.border = "1px solid rgba(255,255,255,0.1)")}>
                  <div className="shrink-0 w-4 h-4 rounded overflow-hidden flex items-center justify-center" style={{ background: "rgba(255,255,255,0.08)" }}>
                    <img src={`https://www.google.com/s2/favicons?sz=32&domain=${faviconHost(r.url)}`} alt="" className="w-4 h-4" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                  </div>
                  <p className="text-xs font-medium truncate flex-1" style={{ color: "#94a3b8" }}>{hostLabel(r.url)}</p>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
  );
};


// Generic terms that appear in use-case bullets but are never company names
const _NOT_COMPANY = new Set([
  "warehouse optimization", "capacity planning", "route optimization",
  "inventory management", "visibility and tracking", "predictive maintenance",
  "demand forecasting", "quality control", "fleet management", "cost reduction",
  "risk management", "real-time monitoring", "digital twin", "process automation",
  "core supply chain ai platforms", "blockchain + supply chain traceability",
  "emerging supply chain saas players", "key players", "top startups",
  "verified startups", "leading startups", "notable startups", "common use cases",
  "service providers", "solution providers", "key features", "why they match",
  "recommendation", "summary", "overview", "conclusion", "use cases",
  "core platforms", "ai platforms", "saas players", "traceability platforms",
]);

// Last words that mark an advantage/benefit phrase, never a company name ending
const _ATTRIBUTE_TAIL = new Set([
  "advantage", "advantages", "stability", "resilience", "efficiency",
  "reduction", "disruption", "traction", "adoption", "scalability",
  "sustainability", "awareness", "dependency", "dependence", "applications",
  "ecosystem", "landscape", "context", "infrastructure", "challenges",
  "opportunities", "dynamics", "drivers", "barriers", "implications",
  "significance", "importance", "benefits", "features", "characteristics",
]);

function _isAttributePhrase(name: string): boolean {
  const lastWord = name.toLowerCase().trim().split(/\s+/).pop() ?? "";
  return _ATTRIBUTE_TAIL.has(lastWord);
}

// ── Startup name extractor ────────────────────────────────────────────────────
function extractStartupNames(text: string): string[] {
  if (!text) return [];
  const names: string[] = [];
  const seen = new Set<string>();
  const NAME_PAT = "([\\.\\w][\\w\\s&.,'\\'\\-/()]{0,55}?)";
  const bulletRx   = new RegExp(`[•\\-\\*]\\s+\\*\\*${NAME_PAT}\\*\\*`, "g");
  const numberedRx = new RegExp(`\\d+\\.\\s+\\*\\*${NAME_PAT}\\*\\*`, "g");
  const headingRx  = new RegExp(`(?:^|\\n)\\*\\*${NAME_PAT}\\*\\*[ \\t]*\\n[-•*]\\s`, "g");
  const inlineRx   = new RegExp(`(?:^|\\n)\\*\\*${NAME_PAT}\\*\\*[ \\t]*[—\\-–:]`, "g");

  for (const rx of [headingRx, bulletRx, numberedRx, inlineRx]) {
    let m: RegExpExecArray | null;
    while ((m = rx.exec(text)) !== null) {
      const name = m[1].trim();
      const key = name.toLowerCase();
      if (!seen.has(key) && name.length > 1 && !_NOT_COMPANY.has(key) && !_isAttributePhrase(name)) {
        seen.add(key);
        names.push(name);
      }
    }
  }
  return names.slice(0, 12);
}

function extractStartupListWithDesc(text: string): Array<{ name: string; description: string }> {
  if (!text) return [];
  const results: Array<{ name: string; description: string }> = [];
  const seen = new Set<string>();
  const NAME_PAT = "([\\.\\w][\\w\\s&.,'\\'\\-/()]{0,55}?)";
  const bulletRx   = new RegExp(`[•\\-\\*]\\s+\\*\\*${NAME_PAT}\\*\\*(?:\\s*[—\\-–:]\\s*([^\\n]*))?`, "g");
  const numberedRx = new RegExp(`\\d+\\.\\s+\\*\\*${NAME_PAT}\\*\\*(?:\\s*[—\\-–:]\\s*([^\\n]*))?`, "g");
  const headingRx  = new RegExp(`(?:^|\\n)\\*\\*${NAME_PAT}\\*\\*[ \\t]*\\n[-•*]\\s+([^\\n]+)`, "g");
  const inlineRx   = new RegExp(`(?:^|\\n)\\*\\*${NAME_PAT}\\*\\*[ \\t]*[—\\-–:]\\s*([^\\n]*)`, "g");

  for (const rx of [headingRx, bulletRx, numberedRx, inlineRx]) {
    let m: RegExpExecArray | null;
    while ((m = rx.exec(text)) !== null) {
      const name = m[1].trim();
      const desc = (m[2] || "").replace(/\*\*/g, "").trim();
      const key = name.toLowerCase();
      if (!seen.has(key) && name.length > 1 && !_NOT_COMPANY.has(key) && !_isAttributePhrase(name)) {
        seen.add(key);
        results.push({ name, description: desc });
      }
    }
  }
  return results.slice(0, 15);
}

// ── Suggestions ───────────────────────────────────────────────────────────────
const SUGGESTIONS = [
  "Top AI startups in India for supply chain",
  "AI use cases in chemical manufacturing",
  "VCs investing in seed-stage AI in India",
  "Best fintech startups to acquire 2025",
];

// ── Quick nav links ────────────────────────────────────────────────────────────
const QUICK_NAV = [
  { label: "Startup Search", to: "/startups", icon: <FiLayers size={13} /> },
  { label: "VC Intelligence", to: "/vc-intelligence", icon: <FiTrendingUp size={13} /> },
  { label: "Use Cases", to: "/discover", icon: <FiCompass size={13} /> },
  { label: "M&A", to: "/acquisition-dashboard", icon: <FiExternalLink size={13} /> },
];

// ── ChatPanel — per-session component ─────────────────────────────────────────
// Mounted with key={sessionId} so React fully remounts for every session switch.
// This gives Claude.ai-style isolation: zero state leakage between sessions.
interface ChatPanelProps {
  sessionId: string;
  initialTurns: Turn[];
  isHistorySession: boolean;
  onPersist: (sessionId: string, turns: Turn[], title: string, ts: number) => void;
  onToggleSidebar: () => void;
  onNewChat: () => void;
}

const ChatPanel = ({ sessionId, initialTurns, isHistorySession, onPersist, onToggleSidebar, onNewChat }: ChatPanelProps) => {
  const navigate = useNavigate();
  const webSearch = useProgressiveSearch();

  const [turns, setTurns] = useState<Turn[]>(initialTurns);
  const [currentQuery, setCurrentQuery] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [input, setInput] = useState("");
  const [editingTurnIndex, setEditingTurnIndex] = useState<number | null>(null);

  const queriedForRef = useRef<string | null>(null);
  const turnIdRef = useRef(0);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const aiRequestIdRef = useRef<string | null>(null);
  // True while background fetch for history session is in progress
  const isLoadingSessionRef = useRef(isHistorySession);
  // True when turns came from history — skip persist until user takes action
  const turnsFromHistoryRef = useRef(isHistorySession);
  // Stable ref to onPersist so the turns effect never captures a stale closure
  const onPersistRef = useRef(onPersist);
  useEffect(() => { onPersistRef.current = onPersist; }, [onPersist]);

  // On mount: focus input; for history sessions fetch fresh turns from server
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 150);

    if (!isHistorySession) return;
    (async () => {
      try {
        const token = await auth.currentUser?.getIdToken() ?? localStorage.getItem("authToken") ?? "";
        if (!token) return;
        // Abort if the user already started a query while we were fetching
        if (queriedForRef.current !== null) return;
        const res = await axios.get(`${BASE_URL}/api/v1/chat/sessions/${sessionId}`, { headers: { Authorization: token } });
        if (queriedForRef.current !== null) return;
        const freshTurns: Turn[] = res.data.turns ?? initialTurns;
        turnsFromHistoryRef.current = true;
        setTurns(freshTurns);
      } catch { /* keep initialTurns */ }
      finally { isLoadingSessionRef.current = false; }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist turns whenever they change — only for user-driven changes
  useEffect(() => {
    if (isLoadingSessionRef.current) return;
    if (turnsFromHistoryRef.current) return;
    if (!turns.length) return;
    const title = turns[0].query.slice(0, 45);
    const ts = Date.now();
    onPersistRef.current(sessionId, turns, title, ts);
  }, [turns, sessionId]);

  // Auto-scroll on new content
  useEffect(() => {
    if (chatScrollRef.current && currentQuery) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [webSearch.results.length, turns.length, currentQuery]);

  // Fire AI call once web results arrive
  useEffect(() => {
    if (!currentQuery) return;
    if (queriedForRef.current === currentQuery) return;
    if (webSearch.results.length === 0) return;
    if (webSearch.phase !== "enriched" && webSearch.phase !== "done") return;
    if (webSearch.activeQuery !== currentQuery) return;
    queriedForRef.current = currentQuery;
    setAiLoading(true);

    const queryAtCall = currentQuery;
    const requestId = crypto.randomUUID();
    aiRequestIdRef.current = requestId;

    const _stopWords = new Set(["in", "for", "and", "the", "of", "to", "a", "an", "with",
      "india", "indian", "startup", "startups", "company", "companies", "best", "top",
      "list", "leading", "new", "latest", "find", "show"]);
    const _queryKws = queryAtCall.toLowerCase().split(/\W+/).filter(w => w.length > 3 && !_stopWords.has(w));
    const _allResults = webSearch.results.slice(0, 14).map(r => ({
      title: r.title, url: r.url, description: r.description, content: r.content, source: r.source,
    }));

    const _aggregatorRe = /\b(top\s+\d+|best\s+\d+|top\s+startup|best\s+startup|leading\s+startup|list\s+of\s+startup|most\s+funded|fastest.growing)\b/i;
    const _isAggregator = (r: { title?: string; url?: string }) =>
      _aggregatorRe.test(r.title || '') ||
      /\/(top|best|leading)[-_]?\d*[-_]?(startup|company|companies)/i.test(r.url || '');

    const _keywordMatches = _queryKws.length > 0
      ? _allResults.filter(r => {
          const text = ((r.title || '') + ' ' + (r.description || '') + ' ' + (r.content || '')).toLowerCase();
          return _queryKws.some(kw => text.includes(kw));
        })
      : _allResults;

    const _clean = _keywordMatches.filter(r => !_isAggregator(r));
    const _nonAggregator = _allResults.filter(r => !_isAggregator(r));
    const sourcesAtCall =
      _clean.length >= 2          ? _clean.slice(0, 10) :
      _keywordMatches.length >= 2 ? _keywordMatches.slice(0, 8) :
      _nonAggregator.length >= 2  ? _nonAggregator.slice(0, 8) :
                                    _allResults.slice(0, 6);

    axios.post(`${BASE_URL}/api/v1/chat/grounded`, { query: queryAtCall, web_results: sourcesAtCall })
      .then(res => {
        if (aiRequestIdRef.current !== requestId) return;
        const raw = (res.data.answer as string) || "";
        const { cleanAnswer, navHint, navRoute, navLabel } = extractNavInfo(raw);
        const turnId = ++turnIdRef.current;
        const newTurn: Turn = {
          id: turnId,
          query: queryAtCall,
          sources: sourcesAtCall,
          cleanAnswer,
          navHint,
          navRoute,
          navLabel,
        };
        setTurns(prev => [...prev, newTurn]);
        setCurrentQuery(null);
        // Extract startup names via Claude asynchronously — chips update after answer appears
        if (cleanAnswer) {
          axios.post(`${BASE_URL}/api/v1/chat/extract-names`, { text: cleanAnswer })
            .then(r => {
              const names: string[] = r.data.names ?? [];
              if (names.length > 0) {
                setTurns(prev => prev.map(t => t.id === turnId ? { ...t, suggestedNames: names } : t));
              }
            })
            .catch(() => {/* keep regex fallback */});
        }
      })
      .catch(() => {
        if (aiRequestIdRef.current !== requestId) return;
        const errorTurn: Turn = {
          id: ++turnIdRef.current,
          query: queryAtCall,
          sources: sourcesAtCall,
          cleanAnswer: "Sorry, I couldn't generate an answer. Browse the sources below.",
          navHint: null,
          navRoute: null,
          navLabel: null,
        };
        setTurns(prev => [...prev, errorTurn]);
        setCurrentQuery(null);
      })
      .finally(() => {
        if (aiRequestIdRef.current === requestId) setAiLoading(false);
      });
  }, [webSearch.results, webSearch.phase, webSearch.activeQuery, currentQuery, sessionId]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleSearch = (text?: string) => {
    const query = (text ?? input).trim();
    if (!query || isSearching || aiLoading) return;
    setInput("");
    turnsFromHistoryRef.current = false;
    isLoadingSessionRef.current = false;
    queriedForRef.current = null;
    setCurrentQuery(query);
    setAiLoading(false);
    webSearch.reset();
    webSearch.search(query, "IN", 20, 5);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleStop = () => {
    webSearch.reset();
    aiRequestIdRef.current = null;
    setCurrentQuery(null);
    setAiLoading(false);
    queriedForRef.current = null;
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleStartEdit = (index: number) => setEditingTurnIndex(index);
  const handleCancelEdit = () => setEditingTurnIndex(null);

  const handleSubmitEdit = (index: number, newQuery: string) => {
    if (!newQuery.trim()) return;
    setTurns(prev => prev.slice(0, index));
    setEditingTurnIndex(null);
    turnsFromHistoryRef.current = false;
    isLoadingSessionRef.current = false;
    aiRequestIdRef.current = null;
    queriedForRef.current = null;
    setAiLoading(false);
    setCurrentQuery(newQuery);
    webSearch.reset();
    webSearch.search(newQuery, "IN", 20, 5);
  };

  const isSearching = webSearch.phase === "serper" || webSearch.phase === "enriched";
  const hasContent = turns.length > 0 || currentQuery !== null;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "#0a0a0a", minWidth: 0 }}>

      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)", flexShrink: 0 }}>
        <button
          className="lg:hidden p-2 rounded-lg"
          style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)", color: "#9ca3af", cursor: "pointer" }}
          onClick={onToggleSidebar}
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {isSearching && (
          <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 14, color: "#f97316" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#f97316", display: "inline-block", animation: "pulse 1s infinite" }} />
            Searching…
          </span>
        )}
        {hasContent && (
          <button
            onClick={onNewChat}
            style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", color: "#9ca3af", fontSize: 14, cursor: "pointer" }}
          >
            <FiEdit size={11} /> New chat
          </button>
        )}
      </div>

      {/* Messages area */}
      <div
        ref={chatScrollRef}
        className="px-4 md:px-16"
        style={{ flex: 1, overflowY: "auto", paddingTop: 32, paddingBottom: 32, scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.1) transparent", display: "flex", flexDirection: "column" }}
      >
        {/* Empty state */}
        {!hasContent && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 28 }}>
            <div style={{ textAlign: "center" }}>
              <h2 style={{ color: "#fff", fontSize: 26, fontWeight: 700, fontFamily: "Sora, sans-serif", marginBottom: 6 }}>What's on your mind?</h2>
            </div>

            {/* Centre search bar */}
            <div style={{ width: "100%", maxWidth: 640, display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
              <div style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 50, padding: "14px 14px 14px 24px" }}>
                <input
                  ref={inputRef as unknown as React.RefObject<HTMLInputElement>}
                  placeholder="Ask anything about startups, VCs, AI use cases…"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !isSearching && !aiLoading) handleSearch(); }}
                  style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "#f9f9f9", fontSize: 15, fontFamily: "Sora, sans-serif" }}
                />
                {isSearching || aiLoading ? (
                  <button
                    onClick={handleStop}
                    title="Stop generating"
                    style={{ width: 38, height: 38, borderRadius: "50%", background: "#FF541F", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.15s" }}
                    onMouseOver={e => (e.currentTarget.style.background = "#e54a1a")}
                    onMouseOut={e => (e.currentTarget.style.background = "#FF541F")}
                  >
                    <div style={{ width: 13, height: 13, borderRadius: 3, background: "#fff" }} />
                  </button>
                ) : (
                  <button
                    onClick={() => handleSearch()}
                    disabled={!input.trim()}
                    style={{ width: 38, height: 38, borderRadius: "50%", background: input.trim() ? "#FF541F" : "rgba(255,255,255,0.08)", border: "none", cursor: input.trim() ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.15s" }}
                  >
                    <FiSend size={15} color="#fff" />
                  </button>
                )}
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", maxWidth: 640 }}>
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => handleSearch(s)}
                    style={{ padding: "10px 16px", borderRadius: 12, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.09)", color: "#d1d5db", fontSize: 14, cursor: "pointer", textAlign: "left" }}
                    onMouseOver={e => { e.currentTarget.style.borderColor = "rgba(255,84,31,0.4)"; e.currentTarget.style.color = "#fff"; }}
                    onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.09)"; e.currentTarget.style.color = "#d1d5db"; }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Completed turns */}
        {turns.map((turn, index) => (
          <TurnBlock
            key={turn.id}
            turn={turn}
            index={index}
            navigate={navigate}
            sessionId={sessionId}
            isEditing={editingTurnIndex === index}
            anyEditing={editingTurnIndex !== null}
            onStartEdit={handleStartEdit}
            onCancelEdit={handleCancelEdit}
            onSubmitEdit={handleSubmitEdit}
          />
        ))}

        {/* In-progress turn */}
        {currentQuery && (
          <div className="mb-6">
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
              <div style={{ maxWidth: "70%", padding: "10px 16px", borderRadius: "18px 18px 4px 18px", fontSize: 14, color: "#fff", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.1)" }}>
                {currentQuery}
              </div>
            </div>

            {webSearch.results.length === 0 && isSearching && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, paddingLeft: 40 }}>
                {[0, 1, 2].map(j => <span key={j} className="block w-1.5 h-1.5 rounded-full bg-orange-500 animate-bounce" style={{ animationDelay: `${j * 0.15}s` }} />)}
                <span style={{ color: "#6b7280", fontSize: 14 }}>Searching the web…</span>
              </div>
            )}

            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <div style={{ width: 28, height: 28, borderRadius: 10, background: "#FF541F", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>AI</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {aiLoading ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {[0, 1, 2].map(j => <span key={j} className="block w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: `${j * 0.15}s` }} />)}
                    <span style={{ color: "#6b7280", fontSize: 14 }}>Analyzing sources…</span>
                  </div>
                ) : webSearch.results.length > 0 ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {[0, 1, 2].map(j => <span key={j} className="block w-2 h-2 rounded-full bg-gray-600 animate-bounce" style={{ animationDelay: `${j * 0.15}s` }} />)}
                    <span style={{ color: "#6b7280", fontSize: 14 }}>Preparing answer…</span>
                  </div>
                ) : null}

                {webSearch.results.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                      <FiSearch size={10} color="#DC2626" />
                      <span style={{ color: "#374151", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Sources · {webSearch.results.length}</span>
                      {isSearching && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#DC2626", display: "inline-block" }} className="animate-pulse" />}
                    </div>
                    <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4, scrollbarWidth: "none" }}>
                      {webSearch.results.slice(0, 8).map((r, idx) => (
                        <a key={idx} href={r.url} target="_blank" rel="noopener noreferrer"
                          style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 10, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", maxWidth: 180, minWidth: 100, textDecoration: "none" }}>
                          <img src={`https://www.google.com/s2/favicons?sz=32&domain=${faviconHost(r.url)}`} alt="" style={{ width: 14, height: 14, borderRadius: 3, flexShrink: 0 }} onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
                          <span style={{ color: "#94a3b8", fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{hostLabel(r.url)}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom follow-up bar — only shown when chat has content */}
      {hasContent && (
        <div className="px-4 md:px-16" style={{ paddingTop: 12, paddingBottom: 16, borderTop: "1px solid rgba(255,255,255,0.06)", background: "rgba(0,0,0,0.4)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 50, padding: "14px 14px 14px 24px", maxWidth: 800, margin: "0 auto" }}>
            <textarea
              ref={inputRef as unknown as React.RefObject<HTMLTextAreaElement>}
              placeholder="Ask a follow-up…"
              value={input}
              rows={1}
              onChange={e => { setInput(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px"; }}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey && !isSearching && !aiLoading) { e.preventDefault(); handleSearch(); } }}
              style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "#f9f9f9", fontSize: 14, fontFamily: "Sora, sans-serif", resize: "none", overflow: "hidden", lineHeight: "1.5" }}
            />
            {isSearching || aiLoading ? (
              <button
                onClick={handleStop}
                title="Stop generating"
                style={{ width: 36, height: 36, borderRadius: "50%", background: "#FF541F", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.15s" }}
                onMouseOver={e => (e.currentTarget.style.background = "#e54a1a")}
                onMouseOut={e => (e.currentTarget.style.background = "#FF541F")}
              >
                <div style={{ width: 13, height: 13, borderRadius: 3, background: "#fff" }} />
              </button>
            ) : (
              <button
                onClick={() => handleSearch()}
                disabled={!input.trim()}
                style={{ width: 36, height: 36, borderRadius: "50%", background: input.trim() ? "#FF541F" : "rgba(255,255,255,0.08)", border: "none", cursor: input.trim() ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.15s" }}
              >
                <FiSend size={15} color="#fff" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── LoggedInView — persistent sidebar wrapper ─────────────────────────────────
// Manages sidebar state and session list. ChatPanel is mounted with key={currentSessionId}
// so switching sessions fully remounts the panel — no state leakage (same as Claude.ai).
const LoggedInView = () => {
  const navigate = useNavigate();

  // Sidebar state
  const [chatHistory, setChatHistory] = useState<ChatSession[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSessionId, setCurrentSessionId] = useState<string>(() => crypto.randomUUID());
  const [currentSessionIsHistory, setCurrentSessionIsHistory] = useState(false);
  const [userName, setUserName] = useState("");
  const [userInitial, setUserInitial] = useState("U");
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [hoveredSessionId, setHoveredSessionId] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Client-side cache: sessionId → turns (survives session switches)
  const turnsCacheRef = useRef<Map<string, Turn[]>>(new Map());

  // Load history & user on mount
  useEffect(() => {
    (async () => {
      const returnSessionId = sessionStorage.getItem("ucdp_return_session_id");
      if (returnSessionId) sessionStorage.removeItem("ucdp_return_session_id");

      const user = auth.currentUser;
      if (user) {
        const name = user.displayName || user.email?.split("@")[0] || "User";
        setUserName(name);
        setUserInitial(name.charAt(0).toUpperCase());
      }

      const token = await auth.currentUser?.getIdToken() ?? localStorage.getItem("authToken") ?? "";
      if (!token) { setHistoryLoading(false); return; }

      axios.get(`${BASE_URL}/api/v1/chat/sessions`, { headers: { Authorization: token } })
        .then(res => {
          const history: ChatSession[] = (res.data.sessions || []).map((s: { id: string; title: string; timestamp: number }) => ({
            id: s.id,
            title: s.title,
            timestamp: s.timestamp,
          }));
          setChatHistory(history);

          // Restore the session the user was viewing before navigating away
          if (returnSessionId) {
            const found = history.find(s => s.id === returnSessionId);
            if (found) {
              setCurrentSessionId(returnSessionId);
              setCurrentSessionIsHistory(true);
            }
          }
        })
        .catch(() => {})
        .finally(() => setHistoryLoading(false));
    })();
  }, []);

  // Called by ChatPanel whenever turns are committed (user-driven)
  const handlePersist = useCallback((sessionId: string, turns: Turn[], title: string, ts: number) => {
    turnsCacheRef.current.set(sessionId, turns);
    const meta: ChatSession = { id: sessionId, title, timestamp: ts };
    setChatHistory(prev => [meta, ...prev.filter(s => s.id !== sessionId)].slice(0, MAX_HISTORY));
    // Fire-and-forget backend save
    (async () => {
      const token = await auth.currentUser?.getIdToken() ?? localStorage.getItem("authToken") ?? "";
      if (token) {
        axios.put(
          `${BASE_URL}/api/v1/chat/sessions/${sessionId}`,
          { title, turns, timestamp: ts },
          { headers: { Authorization: token } }
        ).catch(() => {});
      }
    })();
  }, []);

  const handleNewChat = useCallback(() => {
    setCurrentSessionId(crypto.randomUUID());
    setCurrentSessionIsHistory(false);
    setSidebarOpen(false);
  }, []);

  const loadSession = (session: ChatSession) => {
    if (currentSessionId === session.id) { setSidebarOpen(false); return; }
    setCurrentSessionId(session.id);
    setCurrentSessionIsHistory(true);
    setSidebarOpen(false);
  };

  const deleteSession = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setChatHistory(prev => prev.filter(s => s.id !== id));
    turnsCacheRef.current.delete(id);
    (async () => {
      const token = await auth.currentUser?.getIdToken() ?? localStorage.getItem("authToken") ?? "";
      if (token) {
        axios.delete(`${BASE_URL}/api/v1/chat/sessions/${id}`, { headers: { Authorization: token } }).catch(() => {});
      }
    })();
    if (id === currentSessionId) handleNewChat();
  };

  const filteredHistory = (searchQuery
    ? chatHistory.filter(s => s.title.toLowerCase().includes(searchQuery.toLowerCase()))
    : chatHistory
  ).sort((a, b) => b.timestamp - a.timestamp);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: "#0a0a0a" }}>
    <div style={{ display: "flex", height: "calc(100vh - 70px)", marginTop: 70, overflow: "hidden", position: "relative" }}>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          style={{ top: 70 }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── SIDEBAR ── */}
      <div className={`${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:relative z-40 lg:z-auto transition-transform duration-300`}
        style={{ width: 260, background: "#111", display: "flex", flexDirection: "column", borderRight: "1px solid rgba(255,255,255,0.07)", height: "100%", top: 0, left: 0 }}>

        {/* New Chat button */}
        <div style={{ padding: "12px 10px 8px" }}>
          <button
            onClick={handleNewChat}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 10, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.09)", cursor: "pointer", color: "#d1d5db", fontSize: 14, fontWeight: 500 }}
            onMouseOver={e => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; e.currentTarget.style.color = "#fff"; }}
            onMouseOut={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "#d1d5db"; }}
          >
            <FiEdit size={14} /> New chat
          </button>
        </div>

        {/* Search chats */}
        <div style={{ padding: "4px 10px 8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 8, padding: "7px 10px" }}>
            <FiSearch size={12} color="#6b7280" />
            <input
              placeholder="Search chats"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ background: "transparent", border: "none", outline: "none", color: "#d1d5db", fontSize: 13, width: "100%", fontFamily: "inherit" }}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", padding: 0, display: "flex" }}>
                <FiX size={12} />
              </button>
            )}
          </div>
        </div>

        {/* Recents list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "0 6px" }}>
          {historyLoading && (
            <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
              <div style={{ width: 18, height: 18, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.1)", borderTopColor: "#FF541F", animation: "spin 0.7s linear infinite" }} />
            </div>
          )}
          {!historyLoading && filteredHistory.length > 0 && (
            <p style={{ color: "#6b7280", fontSize: 11, fontWeight: 600, padding: "6px 8px 4px", letterSpacing: "0.05em", textTransform: "uppercase" }}>Recents</p>
          )}
          {!historyLoading && filteredHistory.length === 0 && !searchQuery && (
            <p style={{ color: "#4b5563", fontSize: 13, padding: "16px 10px", textAlign: "center" }}>No recent chats</p>
          )}
          {!historyLoading && filteredHistory.length === 0 && searchQuery && (
            <p style={{ color: "#4b5563", fontSize: 13, padding: "12px 10px", textAlign: "center" }}>No results for "{searchQuery}"</p>
          )}
          {filteredHistory.map(session => (
            <div
              key={session.id}
              onClick={() => loadSession(session)}
              onMouseEnter={() => setHoveredSessionId(session.id)}
              onMouseLeave={() => setHoveredSessionId(null)}
              style={{
                display: "flex", alignItems: "center", gap: 4, padding: "7px 8px",
                borderRadius: 8, cursor: "pointer", marginBottom: 1,
                background: session.id === currentSessionId
                  ? "rgba(255,255,255,0.1)"
                  : hoveredSessionId === session.id
                  ? "rgba(255,255,255,0.05)"
                  : "transparent",
              }}
            >
              <span style={{ flex: 1, color: session.id === currentSessionId ? "#fff" : "#d1d5db", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {session.title}
              </span>
              <button
                onClick={e => deleteSession(e, session.id)}
                title="Delete"
                style={{
                  background: "none", border: "none", cursor: "pointer", padding: "2px 3px",
                  display: "flex", flexShrink: 0, borderRadius: 4, color: "#6b7280",
                  opacity: hoveredSessionId === session.id ? 1 : 0,
                  transition: "opacity 0.15s",
                }}
                onMouseEnter={e => (e.currentTarget.style.color = "#ef4444")}
                onMouseLeave={e => (e.currentTarget.style.color = "#6b7280")}
              >
                <FiTrash2 size={12} />
              </button>
            </div>
          ))}
        </div>

        {/* Quick navigation */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "8px 8px 4px" }}>
          <p style={{ color: "#6b7280", fontSize: 11, fontWeight: 600, padding: "4px 4px 6px", letterSpacing: "0.05em", textTransform: "uppercase" }}>Navigate</p>
          {QUICK_NAV.map(nav => (
            <button
              key={nav.to}
              onClick={() => navigate(nav.to)}
              style={{ width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 8, padding: "7px 8px", borderRadius: 8, background: "transparent", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 14, marginBottom: 2 }}
              onMouseOver={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "#fff"; }}
              onMouseOut={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#9ca3af"; }}
            >
              {nav.icon} {nav.label}
            </button>
          ))}
        </div>

        {/* User info */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", position: "relative" }}>
          {showUserMenu && (
            <div style={{ position: "absolute", bottom: "100%", left: 8, right: 8, background: "#1e1e1e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, overflow: "hidden", marginBottom: 4 }}>
              <div style={{ padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <p style={{ color: "#fff", fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{userName}</p>
                <p style={{ color: "#6b7280", fontSize: 11, marginTop: 2 }}>Signed in</p>
              </div>
              <button
                onClick={async () => {
                  await signOut(auth);
                  localStorage.removeItem("authToken");
                  localStorage.removeItem("uid");
                  localStorage.removeItem("userRole");
                  navigate("/login");
                }}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: 14 }}
                onMouseOver={e => (e.currentTarget.style.background = "rgba(239,68,68,0.1)")}
                onMouseOut={e => (e.currentTarget.style.background = "none")}
              >
                <FiLogOut size={14} /> Sign out
              </button>
            </div>
          )}
          <button
            onClick={() => setShowUserMenu(p => !p)}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "none", border: "none", cursor: "pointer" }}
            onMouseOver={e => (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
            onMouseOut={e => (e.currentTarget.style.background = "none")}
          >
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#FF541F", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
              {userInitial}
            </div>
            <span style={{ color: "#9ca3af", fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, textAlign: "left" }}>{userName}</span>
            <FiChevronUp size={14} color="#6b7280" style={{ transform: showUserMenu ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 0.2s", flexShrink: 0 }} />
          </button>
        </div>
      </div>

      {/* ── CHAT PANEL — key forces full remount on session switch ── */}
      <ChatPanel
        key={currentSessionId}
        sessionId={currentSessionId}
        initialTurns={turnsCacheRef.current.get(currentSessionId) ?? []}
        isHistorySession={currentSessionIsHistory}
        onPersist={handlePersist}
        onToggleSidebar={() => setSidebarOpen(p => !p)}
        onNewChat={handleNewChat}
      />

    </div>

    <style>{`
      @keyframes spin { to { transform: rotate(360deg); } }
      @keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }
      ::-webkit-scrollbar { width: 4px; height: 4px; }
      ::-webkit-scrollbar-track { background: transparent; }
      ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }
      input::placeholder { color: rgba(255,255,255,0.25) !important; }
    `}</style>
    </div>
  );
};

export default LoggedInView;
